import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartLine {
  id: string;
  productId: string;
  variantId?: string;
  name: string;
  slug: string;
  image: string;
  price: number;
  quantity: number;
}

interface CartState {
  isOpen: boolean;
  lines: CartLine[];
  couponCode?: string;
  openCart: () => void;
  closeCart: () => void;
  addLine: (line: Omit<CartLine, 'id'>) => void;
  updateQuantity: (id: string, quantity: number) => void;
  removeLine: (id: string) => void;
  setCoupon: (code?: string) => void;
  clear: () => void;
}

function lineId(productId: string, variantId?: string) {
  return variantId ? `${productId}:${variantId}` : productId;
}

function isValidProductId(id?: string): boolean {
  if (!id) return false;
  const clean = id.trim();
  return clean.length > 0 && clean !== 'undefined' && clean !== 'null' && clean !== '[object Object]';
}

function sanitizeLines(lines: any[]): CartLine[] {
  if (!Array.isArray(lines)) return [];
  return lines
    .filter((l) => l && typeof l === 'object')
    .map((l) => {
      const rawPId = l.productId || l._id || (typeof l.id === 'string' && l.id.includes(':') ? l.id.split(':')[0] : l.id);
      const productId = String(rawPId || '').trim();
      const variantId = l.variantId ? String(l.variantId).trim() : undefined;
      const id = lineId(productId, variantId);
      return {
        id,
        productId,
        variantId,
        name: String(l.name || 'Toothbrush'),
        slug: String(l.slug || 'toothbrush'),
        image: String(l.image || ''),
        price: Number(l.price) || 0,
        quantity: Math.max(1, Number(l.quantity) || 1),
      };
    })
    .filter((l) => isValidProductId(l.productId));
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      isOpen: false,
      lines: [],
      couponCode: undefined,
      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      addLine: (line) => {
        const rawPId = line.productId || (line as any)._id || '';
        const productId = String(rawPId).trim();
        if (!isValidProductId(productId)) return;

        const id = lineId(productId, line.variantId);
        const existing = get().lines.find((l) => l.id === id || l.productId === productId);
        if (existing) {
          set({
            lines: get().lines.map((l) => (l.id === existing.id ? { ...l, quantity: l.quantity + line.quantity } : l)),
            isOpen: true,
          });
        } else {
          set({ lines: [...get().lines, { ...line, id, productId }], isOpen: true });
        }
      },
      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          set({ lines: get().lines.filter((l) => l.id !== id && l.productId !== id) });
          return;
        }
        set({ lines: get().lines.map((l) => (l.id === id || l.productId === id ? { ...l, quantity } : l)) });
      },
      removeLine: (id) => set({ lines: get().lines.filter((l) => l.id !== id && l.productId !== id) }),
      setCoupon: (code) => set({ couponCode: code }),
      clear: () => set({ lines: [], couponCode: undefined }),
    }),
    {
      name: 'orenyva-cart',
      version: 3,
      migrate: (persistedState: any, fromVersion: number) => {
        // Version 3: clear any cart lines whose productId is a real MongoDB ObjectId
        // but no longer exists in the DB (stale after a reseed). We keep only lines
        // whose productId looks like one of the well-known fallback IDs or is a slug.
        if (fromVersion < 3 && persistedState && Array.isArray(persistedState.lines)) {
          // Drop everything from old versions — user will re-add from fresh product pages.
          persistedState.lines = [];
        } else if (persistedState && Array.isArray(persistedState.lines)) {
          persistedState.lines = sanitizeLines(persistedState.lines);
        }
        return persistedState;
      },
      onRehydrateStorage: () => (state) => {
        if (state && Array.isArray(state.lines)) {
          const clean = sanitizeLines(state.lines);
          if (clean.length !== state.lines.length) {
            useCartStore.setState({ lines: clean });
          }
        }
      },
    },
  ),
);

export function useCartTotals() {
  const lines = useCartStore((s) => s.lines);
  const subtotal = lines.reduce((sum, l) => sum + l.price * l.quantity, 0);
  const itemCount = lines.reduce((sum, l) => sum + l.quantity, 0);
  return { subtotal, itemCount };
}
